/* eslint-env browser */
const productTileHelper = require('./helpers/productTile');

const SELECTORS = {
  page: '.page',
  searchResults: '.search-results',
  gridFooter: '.grid-footer',
  productTile: '.product-tile',
  addToBagBtn: '.js-direct-add',
  wishlistBtn: '.product-tile__wishlist--link',
};

/**
 * Extracts cgid from the DOM to use as a Constructor filter.
 *
 * @returns {Array<Object>} Constructor filter list
 */
function getGroupIdFromPageData() {
  const pageElement = document.querySelector(`${SELECTORS.page}[data-querystring]`);
  if (!pageElement) return [];

  const queryString = pageElement.getAttribute('data-querystring');
  const params = new URLSearchParams(queryString);
  const cgidValue = params.get('cgid');

  return cgidValue ? [{
    name: 'group_id',
    value: cgidValue.trim(),
  }] : [];
}

/**
 * Updates the search container with meta data like result count and page number.
 *
 * @param {HTMLElement} container Search result container
 */
function updateResultMeta(container) {
  const products = container.querySelectorAll(SELECTORS.productTile);

  if (products.length === 0) {
    container.dataset.cnstrcZeroResult = 'true';
  } else {
    delete container.dataset.cnstrcZeroResult;
  }

  const total = parseInt(container.dataset.count, 10);
  if (!Number.isNaN(total)) {
    container.dataset.cnstrcNumResults = total;
  }

  const gridFooter = document.querySelector(SELECTORS.gridFooter);
  const page = gridFooter ? parseInt(gridFooter.dataset.pageNumber, 10) : NaN;

  if (!Number.isNaN(page)) {
    container.dataset.cnstrcResultPage = page + 1;
  }
}

/**
 * Applies group_id filters specifically for Browse/Category pages.
 *
 * @param {HTMLElement} container Search result container
 */
function applyBrowseFilters(container) {
  const filters = getGroupIdFromPageData();

  if (!filters.length) return;

  container.dataset.cnstrcFilterName = filters.map(filter => filter.name).join(',');
  container.dataset.cnstrcFilterValue = filters.map(filter => filter.value).join(',');
}

/**
 * Removes old Constructor listing attributes before applying new ones.
 *
 * @param {HTMLElement} container Search result container
 */
function resetListingAttributes(container) {
  container.removeAttribute('data-cnstrc-search');
  container.removeAttribute('data-cnstrc-browse');
  container.removeAttribute('data-cnstrc-filter-name');
  container.removeAttribute('data-cnstrc-filter-value');
}

/**
 * Main logic to toggle between Search and Browse attributes.
 *
 * @param {string} type Listing type: search or browse
 */
function setConstructorListingAttributes(type) {
  const container = document.querySelector(SELECTORS.searchResults);
  if (!container || !type) return;

  resetListingAttributes(container);
  container.setAttribute(`data-cnstrc-${type}`, '');

  updateResultMeta(container);

  if (type === 'browse') {
    applyBrowseFilters(container);
  }
}

/**
 * Returns the latest selected variant ID from the Add to Bag button.
 *
 * @param {HTMLElement} tile Product tile
 * @returns {string} Selected variant ID
 */
function getSelectedVariationId(tile) {
  const addToBagBtn = tile.querySelector(SELECTORS.addToBagBtn);

  return addToBagBtn && addToBagBtn.dataset.pid
    ? addToBagBtn.dataset.pid
    : '';
}

/**
 * Syncs selected variation ID from Add to Bag button to Constructor tile attributes.
 * Checks the data-action attribute to see if an overlay state is pending.
 *
 * @param {HTMLElement} tile Product tile
 */
function syncSelectedVariationId(tile) {
  const currentTileVariationId = tile.dataset.cnstrcItemVariationId;
  const newVariationId = getSelectedVariationId(tile);

  const directAddBtn = tile.querySelector(SELECTORS.addToBagBtn);
  const isOverlayAction = directAddBtn && directAddBtn.dataset.action
    && directAddBtn.dataset.action.includes('Tile-AddToBagOverlay');

  // If the action points to the overlay, or the ID hasn't actually updated yet
  if (isOverlayAction) {
    tile.removeAttribute('data-cnstrc-item-variation-id');
    return;
  }

  if (newVariationId && newVariationId !== currentTileVariationId) {
    tile.dataset.cnstrcItemVariationId = newVariationId;
    productTileHelper.syncBtnAttrs(tile);
  }
}

/**
 * Handles PLP swatch change and updates Constructor attributes.
 *
 * @param {CustomEvent} e Swatch change event
 */
function handleSwatchChange(e) {
  const { tile, selectedSwatch } = e.detail;

  if (!tile || !selectedSwatch) return;

  tile.dataset.cnstrcItemId = selectedSwatch.dataset.productId || '';
  tile.dataset.cnstrcItemName = selectedSwatch.dataset.productName || '';

  tile.removeAttribute('data-cnstrc-item-variation-id');

  // Wait for swatch change DOM update, then read latest variant ID from Add to Bag button
  setTimeout(() => {
    syncSelectedVariationId(tile);
  }, 0);
}

/**
 * Handles PLP size overlay open.
 *
 * @param {CustomEvent} e Overlay toggle event
 */
function handleOverlayToggle(e) {
  const { tile, shown } = e.detail;

  if (shown && tile) {
    productTileHelper.bindPLPSizeSelection(tile);
  }
}

/**
 * Handles Constructor item view event and updates tile attributes.
 *
 * @param {CustomEvent} e Constructor item view event
 */
function handleConstructorItemView(e) {
  const { detail } = e;
  if (!detail) return;

  const productContainer = document.querySelector(`.product[data-pid="${detail.item_id}"]`);
  const tile = productContainer ? productContainer.querySelector(SELECTORS.productTile) : null;

  if (!tile) return;

  tile.dataset.cnstrcItemId = detail.item_id;
  tile.dataset.cnstrcItemName = detail.item_name;
  tile.dataset.cnstrcItemPrice = detail.price;
  tile.dataset.cnstrcPosition = detail.position;

  const wishlistBtn = tile.querySelector(SELECTORS.wishlistBtn);
  if (wishlistBtn) {
    wishlistBtn.dataset.cnstrcBtn = 'add_to_wishlist';
  }

  productTileHelper.syncBtnAttrs(tile);
}

/**
 * Initializes Constructor attributes for PLP/Search tiles.
 */
function initConstructorPLPAttributes() {
  document.addEventListener('tile:swatch-changed', handleSwatchChange);
  document.addEventListener('tile:overlay-toggle', handleOverlayToggle);
  document.addEventListener('constructor:itemView', handleConstructorItemView);
}

/**
 * Initializes Constructor listing attributes.
 */
function initConstructorListing() {
  const page = document.querySelector(SELECTORS.page);
  if (!page) return;

  const query = page.dataset.querystring || '';
  const params = new URLSearchParams(query);

  if (params.has('q')) {
    setConstructorListingAttributes('search');
  } else if (params.has('cgid')) {
    setConstructorListingAttributes('browse');
  }
}

module.exports = function () {
  initConstructorListing();
  initConstructorPLPAttributes();

  productTileHelper.syncBtnAttrs();

  document.addEventListener('constructor:itemViewList', initConstructorListing);
};
